import React, { useEffect } from 'react';
import {
	Button,
	Col,
	Input,
	Row,
	Label,
	ModalBody,
	Form,
	Spinner,
	FormFeedback,
	FormGroup,
} from 'reactstrap';

// Formik
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';

//redux
import { addNewModule, updateModule } from '../../../../slices/settings/thunk';
import { setRequireCloseModal } from '../../../../slices/settings/reducer';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';

const ModuleForm = ({
	dataModule,
	isEdit,
	handleToggleModal,
	isOpen,
}) => {
	const dispatch = useDispatch();

	const selectorModule = createSelector(
		state => state.Settings,
		settings => ({
			isAdding: settings.isAdding,
			isAdded: settings.isAdded,
			isUpdating: settings.isUpdating,
			isUpdated: settings.isUpdated,
			isLoadingList: settings.isLoadingList,
			requireCloseModal: settings.requireCloseModal,
		})
	);

	const {
		isAdding,
		isAdded,
		isUpdating,
		isUpdated,
		requireCloseModal,
	} = useSelector(selectorModule);

	// validation
	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			name: (dataModule && dataModule.name) || '',
			code: (dataModule && dataModule.code) || '',
			description: (dataModule && dataModule.description) || '',
			price: (dataModule && dataModule.price) || '',
			billable: (dataModule && dataModule.billable) !== undefined ? dataModule.billable : true,
			default: (dataModule && dataModule.default) || false,
			level: (dataModule && dataModule.level) || '',
		},
		validationSchema: Yup.object({
			name: Yup.string().required('Campo requerido'),
			code: Yup.string().required('Campo requerido'),
			description: Yup.string().required('Campo requerido'),
			price: Yup.number()
				.required('Campo requerido')
				.positive('El precio debe ser mayor a 0')
				.typeError('El precio debe ser un número'),
			billable: Yup.boolean(),
			default: Yup.boolean(),
			level: Yup.number()
				.required('Campo requerido')
				.positive('El nivel debe ser mayor a 0')
				.integer('El nivel debe ser un número entero')
				.typeError('El nivel debe ser un número'),
		}),
		onSubmit: values => {
			const moduleData = {
				name: values.name,
				code: values.code,
				description: values.description,
				price: parseFloat(values.price),
				billable: values.billable,
				default: values.default,
				level: parseInt(values.level),
			};

			if (isEdit) {
				dispatch(updateModule({ ...moduleData, _id: dataModule._id }));
			} else {
				dispatch(addNewModule(moduleData));
			}
		},
	});

	useEffect(() => {
		if (isOpen) {
			if (requireCloseModal) {
				dispatch(setRequireCloseModal(false));
				handleToggleModal();
			}
		}
	}, [requireCloseModal, isOpen, dispatch, handleToggleModal]);

	return (
		<Form
			className='tablelist-form'
			onSubmit={e => {
				e.preventDefault();
				if (Object.keys(validation.errors).length !== 0) {
					toast.error('Por favor, revisa la información ingresada. Hemos encontrado algunos detalles que necesitan corrección antes de continuar.', { autoClose: 4000 });
				}
				validation.handleSubmit();
				return false;
			}}
		>
			<ModalBody className='modal-body'>
				<Row className='g-3'>
					<Col lg={6}>
						<Label for='name-field' className='form-label'>
							Nombre del Módulo
						</Label>
						<Input
							name='name'
							id='name-field'
							className='form-control'
							placeholder='Ej: Riesgos'
							type='text'
							onChange={validation.handleChange}
							onBlur={validation.handleBlur}
							value={validation.values.name || ''}
							invalid={
								validation.touched.name && validation.errors.name
									? true
									: false
							}
						/>
						{validation.touched.name && validation.errors.name ? (
							<FormFeedback type='invalid'>
								{validation.errors.name}
							</FormFeedback>
						) : null}
					</Col>

					<Col lg={6}>
						<Label for='code-field' className='form-label'>
							Código
						</Label>
						<Input
							name='code'
							id='code-field'
							className='form-control'
							placeholder='Ej: RGS'
							type='text'
							onChange={validation.handleChange}
							onBlur={validation.handleBlur}
							value={validation.values.code || ''}
							invalid={
								validation.touched.code && validation.errors.code
									? true
									: false
							}
						/>
						{validation.touched.code && validation.errors.code ? (
							<FormFeedback type='invalid'>
								{validation.errors.code}
							</FormFeedback>
						) : null}
					</Col>

					<Col lg={12}>
						<Label for='description-field' className='form-label'>
							Descripción
						</Label>
						<Input
							name='description'
							id='description-field'
							className='form-control'
							placeholder='Ej: Gestión de riesgos y prevención de incidentes'
							type='text'
							onChange={validation.handleChange}
							onBlur={validation.handleBlur}
							value={validation.values.description || ''}
							invalid={
								validation.touched.description && validation.errors.description
									? true
									: false
							}
						/>
						{validation.touched.description && validation.errors.description ? (
							<FormFeedback type='invalid'>
								{validation.errors.description}
							</FormFeedback>
						) : null}
					</Col>

					<Col lg={4}>
						<Label for='price-field' className='form-label'>
							Precio (UF)
						</Label>
						<Input
							name='price'
							id='price-field'
							className='form-control'
							placeholder='Ej: 4.0'
							type='number'
							step='0.1'
							min='0'
							onChange={validation.handleChange}
							onBlur={validation.handleBlur}
							value={validation.values.price || ''}
							invalid={
								validation.touched.price && validation.errors.price
									? true
									: false
							}
						/>
						{validation.touched.price && validation.errors.price ? (
							<FormFeedback type='invalid'>
								{validation.errors.price}
							</FormFeedback>
						) : null}
					</Col>

					<Col lg={4}>
						<Label for='level-field' className='form-label'>
							Nivel/Orden
						</Label>
						<Input
							name='level'
							id='level-field'
							className='form-control'
							placeholder='Ej: 10'
							type='number'
							min='1'
							onChange={validation.handleChange}
							onBlur={validation.handleBlur}
							value={validation.values.level || ''}
							invalid={
								validation.touched.level && validation.errors.level
									? true
									: false
							}
						/>
						{validation.touched.level && validation.errors.level ? (
							<FormFeedback type='invalid'>
								{validation.errors.level}
							</FormFeedback>
						) : null}
					</Col>

					<Col lg={4}>
						<FormGroup className='d-flex align-items-center h-100 pt-4'>
							<div className='form-check me-3'>
								<Input
									type='checkbox'
									id='billable-field'
									name='billable'
									checked={validation.values.billable}
									onChange={e => {
										validation.setFieldValue('billable', e.target.checked);
									}}
								/>
								<Label for='billable-field' className='form-check-label ms-2'>
									Facturable
								</Label>
							</div>
							<div className='form-check'>
								<Input
									type='checkbox'
									id='default-field'
									name='default'
									checked={validation.values.default}
									onChange={e => {
										validation.setFieldValue('default', e.target.checked);
									}}
								/>
								<Label for='default-field' className='form-check-label ms-2'>
									Por Defecto
								</Label>
							</div>
						</FormGroup>
					</Col>
				</Row>
			</ModalBody>
			<div className='modal-footer'>
				<div className='hstack gap-2 justify-content-end'>
					<Button
						type='button'
						onClick={() => {
							handleToggleModal();
						}}
						className='btn-danger btn-label'
					>
						<i className='ri-close-line label-icon'></i>
						Cancelar
					</Button>
					<button
						type='submit'
						className='btn btn-label btn-primary'
						id='add-btn'
						disabled={isAdding || isUpdating}
					>
						{isAdding || isUpdating ? (
							<i className='label-icon'><Spinner size='sm' /></i>
						) : (
							<i className='ri-save-3-line label-icon'></i>
						)}
						{isEdit ? 'Actualizar Módulo' : 'Agregar Módulo'}
					</button>
				</div>
			</div>
		</Form>
	);
};

export default ModuleForm;
